import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarClock, DollarSign, Layers, MessageSquare, User } from "lucide-react";
import { StatusBadge, ComplexityBadge } from "./StatusBadge";
import { localized } from "../lib/localized";
import { getCaseRestorations } from "../lib/dental";
import { caseOfferedPrice, fromLabPrice, fmtUSD, DEFAULT_COMMISSION_RATE } from "../lib/pricing";
import { Badge } from "./ui/badge";

export const CaseCard = ({ caseItem, index = 0, applicantCount = 0, unreadCount = 0, patientName = "", netForDesigner = false, aiMatch = null }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  const ai = caseItem.ai;
  const summary = ai ? localized(ai.summary, lang) : caseItem.clinicalInstructions;
  const thumb = caseItem.photos?.[0];
  const restorations = getCaseRestorations(caseItem);
  const matchColor = aiMatch ? (aiMatch.level === "strong" ? "bg-emerald-600" : aiMatch.level === "good" ? "bg-amber-500" : "bg-rose-500") : "";

  return (
    <Link
      to={`/cases/${caseItem.id}`}
      data-testid={`case-card-${caseItem.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {aiMatch && (
        <div className={`flex items-center gap-2 px-3 py-2 text-white ${matchColor}`} data-testid={`ai-match-badge-${caseItem.id}`}>
          <span className="text-xs font-bold uppercase tracking-wide">{t("aiMatch.scoreLabel")}: {aiMatch.score}%</span>
          <span className="rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-semibold">{t(`aiMatch.level.${aiMatch.level}`)}</span>
        </div>
      )}
      {aiMatch?.reason && (
        <p className="border-b border-border bg-secondary/40 px-3 py-1.5 text-[11px] leading-snug text-muted-foreground" data-testid={`ai-match-reason-${caseItem.id}`}>{aiMatch.reason}</p>
      )}
      <div className="relative h-36 w-full overflow-hidden bg-slate-100">
        {thumb ? (
          <img src={thumb} alt={caseItem.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Layers className="h-8 w-8" />
          </div>
        )}
        <div className="absolute right-2 top-2">
          <StatusBadge status={caseItem.status} />
        </div>
        {applicantCount > 0 && (
          <div className="absolute left-2 top-2">
            <Badge className="rounded-full bg-rose-600 text-white shadow hover:bg-rose-600" data-testid={`case-applicants-badge-${caseItem.id}`}>
              {t("lab.interested", { count: applicantCount })}
            </Badge>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-semibold leading-tight tracking-tight text-foreground">
            {caseItem.title}
          </h3>
          {unreadCount > 0 && (
            <Badge className="shrink-0 gap-1 rounded-full bg-brand text-white shadow hover:bg-brand" data-testid={`case-unread-badge-${caseItem.id}`}>
              <MessageSquare className="h-3 w-3" />
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-xs text-muted-foreground">{caseItem.toothNumbers}</span>
          {restorations && restorations.length > 1 && (
            <Badge variant="secondary" className="rounded-full text-[11px] font-medium" data-testid={`case-restorations-count-${caseItem.id}`}>
              {t("restoration.count", { count: restorations.length })}
            </Badge>
          )}
          {ai?.complexity && <ComplexityBadge complexity={ai.complexity} />}
        </div>

        {(caseItem.caseNumber || patientName) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {caseItem.caseNumber && (
              <span className="font-mono text-muted-foreground" data-testid={`case-number-${caseItem.id}`}>{caseItem.caseNumber}</span>
            )}
            {patientName && (
              <span className="inline-flex items-center gap-1 font-medium text-foreground" data-testid={`case-patient-${caseItem.id}`}>
                <User className="h-3 w-3 text-muted-foreground" /> {patientName}
              </span>
            )}
          </div>
        )}

        <p className="line-clamp-2 flex-1 text-sm text-muted-foreground">{summary}</p>

        {ai?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {ai.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="rounded-full text-[11px] font-medium">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1" data-testid={`case-price-${caseItem.id}`}>
            <DollarSign className="h-3.5 w-3.5" />
            {(() => {
              const offered = caseOfferedPrice(caseItem);
              if (!offered) return "—";
              if (netForDesigner) {
                const net = fromLabPrice(offered, DEFAULT_COMMISSION_RATE).designerNet;
                return <span title={t("pricing.youWillReceive")}>{fmtUSD(net)}</span>;
              }
              return fmtUSD(offered);
            })()}
          </span>
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            {caseItem.deadline || "—"}
          </span>
        </div>
      </div>
    </Link>
  );
};
